/**
 * Huquqiy hujjatlar: Foydalanish shartlari, Maxfiylik siyosati, Cookie siyosati.
 *
 * DIQQAT: bu professional shablon — ishga tushirishdan oldin YURIST ko'rib chiqishi shart
 * (ayniqsa voyaga yetmagan o'quvchilar ma'lumotlari bilan ishlanadi).
 *
 * ALOQA EMAIL'I QATTIQ YOZILMAYDI. Har maktab (tenant) o'z support email'ini
 * Sozlamalarda kiritadi (`Tenant.supportEmail`, `GET /tenant/support-email`) va
 * hujjatdagi "Savollar uchun: …" qatorida AYNAN SHU maktabning manzili chiqadi.
 * Email yo'q bo'lsa (kiritilmagan, login'dan oldin ochilgan sahifa, eski backend)
 * qator neytral matnga ("Maktab ma'muriyatiga murojaat qiling") almashadi —
 * platformaning umumiy manzili ham, boshqa maktabniki ham o'rniga QO'YILMAYDI.
 *
 * ISTISNO — DASTURCHI ALOQASI (2026-10-17, Google Play talabi): Maxfiylik siyosatida
 * ilova nomi (`APP_NAME`), dasturchi (`DEVELOPER_*`) va DOIMIY aloqa email'i
 * (`PRIVACY_CONTACT_EMAIL`) oddiy matn sifatida turadi — login'siz ham ko'rinadi.
 * Qiymatlar `lib/brand.ts` da; maktab email'i (bor bo'lsa) ular yonida qo'shimcha chiqadi.
 * Ilovadagi nusxa (app `src/legal/content.ts`) bilan matn bir xil bo'lishi kerak.
 */

import { PREMIUM_UI_ENABLED } from '../lib/features';
import {
  APP_NAME, DEVELOPER_NAME, DEVELOPER_URL, PRIVACY_CONTACT_EMAIL, PANEL_SITE, DELETE_ACCOUNT_URL,
} from '../lib/brand';

/**
 * Aloqa email'i kerak bo'lgan qator. Email bor bo'lsa `withContact(email)`,
 * yo'q bo'lsa `without` (kontaktsiz neytral matn).
 */
export type LegalContactLine = { withContact: (email: string) => string; without: string };
type LegalLine = string | LegalContactLine;
const contactLine = (withContact: (e: string) => string, without: string): LegalContactLine => ({
  withContact,
  without,
});

export type LegalKey = 'terms' | 'privacy' | 'cookies';

/** Manba bo'lim — qatorlarning ba'zilari aloqa email'iga bog'liq (`LegalContactLine`). */
type LegalSectionSource = {
  title: string;
  body: LegalLine[];
  /**
   * PREMIUM MUZLATISH: faqat `PREMIUM_UI_ENABLED === true` bo'lganda `body` oxiriga
   * qo'shiladigan bandlar. Matn O'CHIRILMAYDI — bayroq qaytarilishi bilan hujjatga
   * o'z-o'zidan qaytadi. Sahifalar faqat `body` ni chizadi, shuning uchun muzlatilgan
   * paytda bu bandlar hech qayerda ko'rinmaydi.
   * Ilova versiyasi bilan AYNAN bir xil (`app/src/legal/content.ts`) — huquqiy hujjat
   * ikki platformada zid bo'lib qolmasin.
   */
  premiumBody?: LegalLine[];
};
type LegalDocSource = { title: string; updated: string; intro: string; sections: LegalSectionSource[] };

/** Sahifa chizadigan (aloqa email'i hal qilingan) bo'lim va hujjat. */
export type LegalSection = { title: string; body: string[] };
export type LegalDoc = { title: string; updated: string; intro: string; sections: LegalSection[] };

/** Hujjatlar oxirgi yangilangan sana va sayt nomi — bitta joyda (aloqa email'i tenantdan keladi) */
export const LEGAL_UPDATED = '2026-10-17';
export const LEGAL_SITE = PANEL_SITE;

type Lang = 'uz' | 'ru' | 'en';

export const LEGAL: Record<Lang, Record<LegalKey, LegalDocSource>> = {
  // ─────────────────────────── O'ZBEKCHA ───────────────────────────
  uz: {
    terms: {
      title: 'Foydalanish shartlari',
      updated: LEGAL_UPDATED,
      intro:
        `${APP_NAME} ("Platforma") — maktab boshqaruvi uchun mo'ljallangan dasturiy xizmat (dasturchi: ${DEVELOPER_NAME}, ${DEVELOPER_URL}). Platformadan foydalanish orqali siz ushbu shartlarga rozilik bildirasiz. Rozi bo'lmasangiz, xizmatdan foydalanmang.`,
      sections: [
        {
          title: '1. Atamalar',
          body: [
            `"Platforma" — ${APP_NAME} mobil ilovasi va ${LEGAL_SITE} boshqaruv paneli.`,
            `"Maktab" — Platformadan foydalanuvchi ta’lim muassasasi.`,
            `"Foydalanuvchi" — Maktab tomonidan hisob berilgan shaxs (rahbar, admin, buxgalter, o’qituvchi, ota-ona).`,
            `"Kontent" — Platformaga kiritilgan har qanday ma’lumot (o’quvchi ma’lumotlari, baholar, davomat, to’lovlar va h.k.).`,
          ],
        },
        {
          title: '2. Xizmat tavsifi',
          body: [
            `Platforma maktabga o’quvchilar, sinflar, dars jadvali, davomat, baholar, uy vazifalari, to’lovlar, xarajatlar va xodimlarni boshqarish imkonini beradi.`,
            `Xizmat "boricha" (as-is) taqdim etiladi. Biz uzluksiz ishlashga harakat qilamiz, lekin texnik ishlar yoki uchinchi tomon xizmatlari tufayli vaqtincha uzilishlar bo’lishi mumkin.`,
          ],
        },
        {
          title: '3. Hisoblar va kirish',
          body: [
            `Foydalanuvchi hisoblarini Maktab yaratadi va boshqaradi. Ochiq ro’yxatdan o’tish yo’q.`,
            `Login va parolni maxfiy saqlash — Foydalanuvchi mas’uliyati. Hisobingizdan amalga oshirilgan barcha harakatlar uchun siz javobgarsiz.`,
            `Hisob buzilganini sezsangiz, darhol Maktab ma’muriyatiga xabar bering.`,
          ],
        },
        {
          title: '4. Foydalanuvchi majburiyatlari',
          body: [
            `Faqat o’z rolingiz doirasidagi ma’lumotlarga kiring va ularni faqat ta’lim maqsadida ishlating.`,
            `Boshqa shaxslarning shaxsiy ma’lumotlarini ruxsatsiz tarqatmang, nusxalamang yoki uchinchi tomonga bermang.`,
            `Kiritilayotgan ma’lumotlarning to’g’riligi uchun ma’lumot kiritgan Foydalanuvchi va Maktab javobgardir.`,
          ],
        },
        {
          title: '5. Taqiqlangan harakatlar',
          body: [
            `Platformaga ruxsatsiz kirishga urinish, zaifliklarni ekspluatatsiya qilish, avtomatlashtirilgan yig’ish (scraping), tizimga ortiqcha yuk berish.`,
            `Boshqa foydalanuvchi nomidan ish ko’rish, hisobni uchinchi shaxsga berish yoki sotish.`,
            `Qonunga zid, haqoratli yoki bolalarga zarar yetkazadigan kontent joylash.`,
          ],
        },
        {
          title: '6. Intellektual mulk',
          body: [
            `Platformaning dasturiy kodi, dizayni va brendi bizga tegishli. Ularni nusxalash yoki qayta ishlatish taqiqlanadi.`,
            `Maktab kiritgan Kontent Maktabga tegishli bo’lib qoladi. Biz uni faqat xizmatni taqdim etish uchun ishlatamiz.`,
          ],
        },
        {
          title: '7. To’lovlar va obuna',
          body: [
            `Platformadan foydalanish Maktab bilan tuzilgan shartnoma asosida to’lanadi.`,
            `To’lov shartlari, muddati va bekor qilish tartibi alohida kelishiladi. To’lov amalga oshirilmasa, xizmat vaqtincha to’xtatilishi mumkin.`,
          ],
          premiumBody: [
            `Ota-onalar uchun ba’zi qo’shimcha imkoniyatlar (Premium) alohida taklif etilishi mumkin.`,
          ],
        },
        {
          title: '8. Mas’uliyatni cheklash',
          body: [
            `Biz Kontentning to’g’riligi (baholar, davomat, to’lov summalari) uchun javobgar emasmiz — uni Maktab kiritadi.`,
            `Bilvosita zarar, foyda yo’qotilishi yoki ma’lumotlar yo’qolishi uchun mas’uliyat qonun ruxsat etgan darajada cheklanadi.`,
            `Foydalanuvchi tomonidan parolning oshkor bo’lishi natijasidagi zarar uchun javobgar emasmiz.`,
          ],
        },
        {
          title: '9. Xizmatni to’xtatish',
          body: [
            `Shartlarni buzgan hisob ogohlantirishsiz bloklanishi mumkin.`,
            `Maktab shartnomani bekor qilsa, ma’lumotlar Maxfiylik siyosatidagi muddat bo’yicha o’chiriladi yoki qaytariladi.`,
          ],
        },
        {
          title: '10. O’zgartirishlar',
          body: [
            `Ushbu shartlar vaqti-vaqti bilan yangilanishi mumkin. Muhim o’zgarishlar haqida Platforma orqali xabar beramiz.`,
            `Yangilanishdan keyin foydalanishda davom etish — yangi shartlarga rozilik hisoblanadi.`,
          ],
        },
        {
          title: '11. Qo’llaniladigan huquq va aloqa',
          body: [
            `Ushbu shartlarga O’zbekiston Respublikasi qonunchiligi qo’llaniladi. Nizolar avvalo muzokara yo’li bilan hal qilinadi.`,
            contactLine((e) => `Savollar uchun: ${e}`, `Savollar bo’yicha Maktab ma’muriyatiga murojaat qiling.`),
          ],
        },
      ],
    },

    privacy: {
      title: 'Maxfiylik siyosati',
      updated: LEGAL_UPDATED,
      intro:
        `Ushbu siyosat ${APP_NAME} mobil ilovasi va maktab boshqaruv paneli (${LEGAL_SITE}) — birgalikda "Platforma" — qanday shaxsiy ma’lumotlarni yig’ishi, ishlatishi, kimga uzatishi, qancha saqlashi va qanday o’chirishini tushuntiradi. Dasturchi: ${DEVELOPER_NAME} (${DEVELOPER_URL}). Platformada voyaga yetmagan o’quvchilar ma’lumotlari ishlanadi — shuning uchun himoyaga alohida e’tibor beramiz.`,
      sections: [
        {
          title: '1. Ilova, dasturchi va ma’lumot nazoratchisi',
          body: [
            `${APP_NAME} ilovasi va Platformaning dasturchisi — ${DEVELOPER_NAME} (${DEVELOPER_URL}). Maxfiylik va akkauntni o’chirish bo’yicha doimiy aloqa: ${PRIVACY_CONTACT_EMAIL}.`,
            `O’quvchi, ota-ona va xodim ma’lumotlarining egasi va nazoratchisi — Maktab. Biz Maktab nomidan ma’lumotlarni qayta ishlovchi (protsessor) sifatida ish ko’ramiz.`,
            `Ochiq ro’yxatdan o’tish yo’q: login va parolni Maktab beradi, hisoblarni Maktab boshqaradi.`,
            `Ma’lumotni tuzatish so’rovlari birinchi navbatda Maktabga yuboriladi.`,
          ],
        },
        {
          title: '2. Qanday ma’lumotlarni yig’amiz',
          body: [
            `Shaxsiy: F.I.Sh., jinsi, tug’ilgan sana, telefon, manzil, profil rasmi, Telegram ID (Maktab kiritgan bo’lsa).`,
            `Hisob: login, parol (faqat shifrlangan xesh ko’rinishida — ochiq parolni saqlamaymiz), rol.`,
            `Ta’limga oid: sinf, guruh, davomat, baholar, uy vazifalari, dars jadvali, xulq va test ballari, reyting.`,
            `Moliyaviy: shartnomalar, to’lovlar, qarzdorlik, xodim maoshi.`,
            `Shartnomani onlayn imzolashda ota-ona o’zi kiritadi: F.I.Sh., pasport seriyasi va raqami, JSHSHIR, yashash manzili, telefon va qo’lda chizilgan imzo rasmi. Huquqiy dalil sifatida imzolash paytidagi IP manzil va qurilma/brauzer ma’lumoti (User-Agent) ham saqlanadi. Shartnoma rad etilsa — ota-ona yozgan rad etish sababi saqlanadi.`,
            `Yordam (support) chati: ota-ona va Maktab ma’muriyati o’rtasidagi xabarlar.`,
            `Texnik: push-bildirishnoma tokeni, til va mavzu sozlamalari, xatolik loglari.`,
            `Audit (xavfsizlik) jurnali: ma’lumotni o’zgartiruvchi har bir amal — kim bajargani (login, rol), qaysi amal, vaqti, IP manzil va qurilma/brauzer ma’lumoti (User-Agent).`,
            `FACE ID (faqat Maktab xodimlari uchun): yuz tanish terminali Maktab binosida o’rnatilgan. Yuzning biometrik shabloni faqat shu terminal xotirasida saqlanadi va serverga yuborilmaydi; terminal hodisa bilan birga yuborgan rasm serverda saqlanmaydi. Serverga faqat kelish-ketish hodisasi (xodim raqami va vaqti) keladi, terminalga esa xodimning ismi va raqami yuboriladi.`,
          ],
        },
        {
          title: '3. Ilova ruxsatlari',
          body: [
            `Kamera va galereya — faqat profil rasmini olish yoki tanlash uchun. Rasm faqat siz tanlab tasdiqlaganingizda yuklanadi; kamera fonda ishlatilmaydi.`,
            `Bildirishnomalar — baho, davomat, e’lon, to’lov va boshqa xabarlar uchun. Ruxsatni istalgan vaqtda qurilma sozlamalaridan o’chirishingiz mumkin.`,
            `Ilova joylashuv, kontaktlar yoki mikrofonga ruxsat so’ramaydi.`,
          ],
        },
        {
          title: '4. Nima uchun ishlatamiz',
          body: [
            `Xizmatni taqdim etish: davomat, baho, jadval, to’lovlar va shartnomalarni yuritish.`,
            `Bildirishnomalar yuborish (baho, davomat, e’lon, to’lov eslatmasi).`,
            `Qo’llab-quvvatlash, xavfsizlik (audit jurnali) va xatoliklarni tuzatish.`,
            `Ma’lumotlarni reklama maqsadida ISHLATMAYMIZ va uchinchi tomonga SOTMAYMIZ.`,
          ],
        },
        {
          title: '5. Bolalar ma’lumotlari',
          body: [
            `O’quvchilar ma’lumotlarini Platformaga Maktab kiritadi, bevosita bolalardan yig’maymiz. ${APP_NAME} ilovasidan ota-ona va o’qituvchilar foydalanadi.`,
            `Bolalar ma’lumotlari faqat ta’lim jarayoni uchun ishlatiladi va faqat vakolatli shaxslarga (Maktab xodimlari, o’z farzandi bo’yicha ota-ona) ko’rinadi.`,
            `Bolalar ma’lumotlari hech qachon reklama yoki profillash uchun ishlatilmaydi.`,
          ],
        },
        {
          title: '6. Kim ko’ra oladi',
          body: [
            `Kirish ROL asosida cheklanadi: ota-ona faqat o’z farzandini, o’qituvchi faqat o’z sinf/guruhini ko’radi.`,
            `Reyting: ota-onaga farzandi qatnashgan reyting jadvalida boshqa o’quvchilarning ismi, sinfi va reyting bali ko’rinadi; farzandining sinfdoshlari uchun esa profil rasmi va reyting tafsiloti (o’rtacha baho, baholar soni, fanlar, davomat, xulq, test va uy vazifasi bo’yicha ball) ham ko’rinadi.`,
            `Maktab yoqsa, bino ichidagi ekranda (kiosk) o’quvchilar reytingi va tug’ilgan kun egalari (ism, sinf, rasm), dars jadvali va e’lonlar ko’rsatiladi.`,
            `Har bir Maktab ma’lumoti boshqasidan texnik jihatdan AJRATILGAN (tenant izolyatsiyasi) — bir maktab boshqasining ma’lumotini ko’ra olmaydi.`,
          ],
        },
        {
          title: '7. Uchinchi tomon xizmatlari',
          body: [
            `Server, ma’lumotlar bazasi va fayl xotirasi (profil rasmlari, hujjatlar): Railway (S3-mos obyekt xotirasi bilan).`,
            `Domen nomlari (DNS): Cloudflare.`,
            `Push-bildirishnomalar: Expo Push xizmati va u orqali Google Firebase Cloud Messaging (Android) va Apple Push Notification service (iOS) — qurilma tokeni va bildirishnoma matni uzatiladi.`,
            `SMS (Maktab uchun yoqilgan bo’lsa, masalan shartnoma havolasi): Eskiz.uz — telefon raqami va SMS matni uzatiladi.`,
            `Telegram: ma’lumotlar bazasining kunlik zaxira nusxasi va texnik nosozlik ogohlantirishlari yopiq Telegram guruhlariga yuboriladi; Maktab o’z ma’lumotlari zaxirasini o’z Telegram botiga yuborishni yoqishi mumkin.`,
            `Xatoliklarni kuzatish (yoqilgan bo’lsa): Sentry — faqat server xatoligi tafsilotlari.`,
            `Ma’lumot faqat xizmatni ko’rsatish uchun kerakli hajmda uzatiladi. Reklama yoki analitika tarmoqlariga uzatilmaydi va sotilmaydi.`,
          ],
        },
        {
          title: '8. Saqlash muddati',
          body: [
            `Ma’lumotlar Maktab bilan shartnoma amal qilgan davrda yoki Maktab ularni o’chirguncha saqlanadi.`,
            `Audit (xavfsizlik) jurnali — 90 kundan keyin avtomatik o’chiriladi.`,
            `Push-bildirishnoma tokeni — ilova o’chirilgani aniqlanganda yoki akkauntni o’chirish so’rovi yuborilganda o’chiriladi.`,
            `Imzolangan shartnoma (imzo, IP manzil va User-Agent bilan) — Maktab hujjati sifatida Maktab belgilagan muddatda saqlanadi.`,
            `Zaxira nusxalar yopiq Telegram guruhlarida saqlanadi va avtomatik o’chirilmaydi — ular ma’muriyat belgilagan tartibda tozalanadi. O’chirilgan ma’lumot zaxira nusxalarda ular tozalanguncha qolishi mumkin.`,
            `Maktab bilan shartnoma tugagach, ma’lumotlar Maktab talabiga ko’ra qaytariladi yoki oqilona muddat ichida o’chiriladi (qonun talab qilgan holatlar bundan mustasno).`,
          ],
        },
        {
          title: '9. Akkauntni va ma’lumotlarni o’chirish',
          body: [
            `Ilovada: Profil → Akkaunt → «Akkauntni o’chirish» (parol bilan tasdiqlanadi).`,
            `Ilovasiz: ${DELETE_ACCOUNT_URL} sahifasida login va parolingiz bilan.`,
            `So’rov yuborilishi bilan akkaunt darhol yopiladi: unga kirib bo’lmaydi, push-bildirishnomalar to’xtaydi. Maktab ma’muriyati so’rovni 30 kun ichida yakunlaydi.`,
            `O’chiriladi: login, parol, ism, telefon, jins, tug’ilgan sana, profil rasmi, Telegram ID, qo’shimcha telefon va manzillar, push tokenlar, ilova ichidagi bildirishnomalar va yordam chatidagi yozishmalar.`,
            `Maktab yozuvi sifatida qoladi (ism o’rniga «O’chirilgan foydalanuvchi» bilan): o’quvchining baholari, davomati, to’lovlari va shartnomalari (imzolangan shartnomadagi ota-ona ma’lumotlari bilan), o’quvchi kartochkasidagi ota-ona kontakti; o’qituvchi bo’lsa — qo’ygan baholari, uy vazifalari, dars jadvali, maosh va ish vaqti yozuvlari.`,
            `Audit jurnali 90 kunda, zaxira nusxalar esa ular tozalanganda o’chadi. Xodimning FACE ID terminalidagi yozuvi bu so’rov bilan o’chmaydi — uni Maktab ma’muriyati o’chiradi.`,
            `Maktab so’rovni sabab ko’rsatib rad etishi mumkin — bunda akkaunt avvalgi holatiga qaytadi va sizga sabab bilan xabar keladi.`,
            `Farzandingiz (o’quvchi) haqidagi ma’lumotni o’chirish yoki tuzatish uchun Maktab ma’muriyatiga murojaat qiling.`,
          ],
        },
        {
          title: '10. Xavfsizlik',
          body: [
            `Parollar shifrlangan xesh (bcrypt) ko’rinishida saqlanadi.`,
            `Barcha aloqa HTTPS orqali shifrlanadi.`,
            `Rolga asoslangan kirish nazorati va maktablararo ma’lumot izolyatsiyasi qo’llaniladi.`,
            `Hech bir tizim 100% xavfsiz emas — shubhali holatni sezsangiz darhol xabar bering.`,
          ],
        },
        {
          title: '11. Sizning huquqlaringiz',
          body: [
            `O’zingiz (yoki farzandingiz) haqidagi ma’lumotni bilish, noto’g’risini tuzattirish va o’chirishni so’rash huquqiga egasiz (akkauntni o’chirish — 9-bo’lim).`,
            `Bildirishnomalarni istalgan vaqtda ilova yoki qurilma sozlamalaridan o’chirishingiz mumkin.`,
            contactLine(
              (e) => `So’rovni Maktab ma’muriyatiga, ${e} yoki ${PRIVACY_CONTACT_EMAIL} manziliga yuboring.`,
              `So’rovni Maktab ma’muriyatiga yoki ${PRIVACY_CONTACT_EMAIL} manziliga yuboring.`
            ),
          ],
        },
        {
          title: '12. Cookie va lokal saqlash',
          body: [
            `Veb-versiyada brauzer xotirasi (localStorage), mobil ilovada qurilma xotirasi ishlatiladi. Batafsil — "Cookie siyosati" bo’limida.`,
          ],
        },
        {
          title: '13. O’zgartirishlar va aloqa',
          body: [
            `Siyosat yangilanishi mumkin; oxirgi yangilangan sana yuqorida ko’rsatilgan.`,
            `Dasturchi (${DEVELOPER_NAME}) bilan aloqa: ${PRIVACY_CONTACT_EMAIL}.`,
            contactLine((e) => `Maktabingiz bilan aloqa: ${e}`, `Maktabingiz bo’yicha savollar uchun Maktab ma’muriyatiga murojaat qiling.`),
          ],
        },
      ],
    },

    cookies: {
      title: 'Cookie siyosati',
      updated: LEGAL_UPDATED,
      intro:
        `Ushbu siyosat ${APP_NAME} veb-panelida (${LEGAL_SITE}) cookie va brauzer xotirasi (localStorage), mobil ilovada esa qurilma xotirasi qanday ishlatilishini tushuntiradi.`,
      sections: [
        {
          title: '1. Cookie va lokal xotira nima',
          body: [
            `Cookie — sayt brauzeringizda saqlaydigan kichik matn fayli. localStorage — shunga o’xshash, lekin sayt tomonidan boshqariladigan brauzer xotirasi.`,
            `Biz asosan localStorage ishlatamiz — sizni tizimda "eslab qolish" va sozlamalaringizni saqlash uchun.`,
          ],
        },
        {
          title: '2. Biz nimani saqlaymiz',
          body: [
            `Kirish tokeni (sata_token) — tizimda qolishingiz uchun. ZARURIY.`,
            `Foydalanuvchi ma’lumoti (sata_user) — ism/rol ko’rsatish uchun. ZARURIY.`,
            `Til va mavzu (yorug’/tungi) tanlovi — qulaylik uchun.`,
            `Mobil ilovada: kirish tokeni, push-bildirishnoma tokeni va sozlamalar qurilma xotirasida saqlanadi.`,
          ],
        },
        {
          title: '3. Reklama va kuzatuv YO’Q',
          body: [
            `Biz reklama cookie’lari, kuzatuv piksellari yoki uchinchi tomon analitikasidan FOYDALANMAYMIZ.`,
            `Ma’lumotingiz reklama tarmoqlariga uzatilmaydi.`,
          ],
        },
        {
          title: '4. Uchinchi tomon',
          body: [
            `Cloudflare xavfsizlik va tezlik uchun texnik cookie qo’yishi mumkin (bot himoyasi). Bu kuzatuv uchun emas.`,
          ],
        },
        {
          title: '5. Qanday boshqarish mumkin',
          body: [
            `Brauzer sozlamalaridan cookie/localStorage’ni tozalashingiz mumkin.`,
            `DIQQAT: zaruriy saqlashni o’chirsangiz, tizimdan chiqib ketasiz va Platforma to’g’ri ishlamaydi.`,
            `Mobil ilovada — ilovani o’chirish yoki qurilma sozlamalaridan ma’lumotlarni tozalash orqali.`,
          ],
        },
        {
          title: '6. Rozilik',
          body: [
            `Biz faqat ZARURIY saqlashdan foydalanamiz — usiz tizimga kirib bo’lmaydi. Platformadan foydalanish orqali bunga rozilik bildirasiz.`,
            contactLine((e) => `Savollar: ${e}`, `Savollar bo’yicha Maktab ma’muriyatiga murojaat qiling.`),
          ],
        },
      ],
    },
  },

  // ─────────────────────────── РУССКИЙ ───────────────────────────
  ru: {
    terms: {
      title: 'Условия использования',
      updated: LEGAL_UPDATED,
      intro:
        `${APP_NAME} («Платформа») — программный сервис для управления школой (разработчик: ${DEVELOPER_NAME}, ${DEVELOPER_URL}). Используя Платформу, вы соглашаетесь с настоящими условиями. Если вы не согласны — не используйте сервис.`,
      sections: [
        {
          title: '1. Термины',
          body: [
            `«Платформа» — мобильное приложение ${APP_NAME} и панель управления ${LEGAL_SITE}.`,
            '«Школа» — образовательное учреждение, использующее Платформу.',
            '«Пользователь» — лицо, которому Школа выдала учётную запись (директор, админ, бухгалтер, учитель, родитель).',
            '«Контент» — любые данные, внесённые в Платформу (данные учеников, оценки, посещаемость, платежи и т.д.).',
          ],
        },
        {
          title: '2. Описание сервиса',
          body: [
            'Платформа позволяет вести учеников, классы, расписание, посещаемость, оценки, домашние задания, платежи, расходы и сотрудников.',
            'Сервис предоставляется «как есть». Мы стремимся к бесперебойной работе, но возможны временные перерывы из-за техработ или сторонних сервисов.',
          ],
        },
        {
          title: '3. Учётные записи и доступ',
          body: [
            'Учётные записи создаёт и управляет ими Школа. Открытой регистрации нет.',
            'Сохранность логина и пароля — ответственность Пользователя. Вы отвечаете за все действия под вашей учётной записью.',
            'При подозрении на взлом немедленно сообщите администрации Школы.',
          ],
        },
        {
          title: '4. Обязанности пользователя',
          body: [
            'Заходите только в данные в рамках своей роли и используйте их только в образовательных целях.',
            'Не распространяйте, не копируйте и не передавайте третьим лицам персональные данные других людей.',
            'За достоверность вносимых данных отвечают внёсший Пользователь и Школа.',
          ],
        },
        {
          title: '5. Запрещённые действия',
          body: [
            'Попытки несанкционированного доступа, эксплуатация уязвимостей, автоматический сбор данных (scraping), перегрузка системы.',
            'Действия от имени другого пользователя, передача или продажа учётной записи.',
            'Размещение противоправного, оскорбительного или вредного для детей контента.',
          ],
        },
        {
          title: '6. Интеллектуальная собственность',
          body: [
            'Код, дизайн и бренд Платформы принадлежат нам. Копирование и повторное использование запрещены.',
            'Контент, внесённый Школой, остаётся собственностью Школы. Мы используем его только для оказания услуги.',
          ],
        },
        {
          title: '7. Оплата и подписка',
          body: [
            'Использование Платформы оплачивается по договору со Школой.',
            'Условия, сроки и порядок отмены оплаты согласуются отдельно. При неоплате сервис может быть приостановлен.',
          ],
          premiumBody: [
            'Для родителей могут предлагаться дополнительные возможности (Premium).',
          ],
        },
        {
          title: '8. Ограничение ответственности',
          body: [
            'Мы не отвечаем за достоверность Контента (оценки, посещаемость, суммы платежей) — его вносит Школа.',
            'Ответственность за косвенный ущерб, упущенную выгоду или потерю данных ограничена в пределах, допустимых законом.',
            'Мы не отвечаем за ущерб от разглашения пароля самим Пользователем.',
          ],
        },
        {
          title: '9. Приостановка сервиса',
          body: [
            'Учётная запись, нарушившая условия, может быть заблокирована без предупреждения.',
            'При расторжении договора Школой данные удаляются или возвращаются в сроки, указанные в Политике конфиденциальности.',
          ],
        },
        {
          title: '10. Изменения',
          body: [
            'Условия могут периодически обновляться. О существенных изменениях мы сообщим через Платформу.',
            'Продолжение использования после обновления означает согласие с новыми условиями.',
          ],
        },
        {
          title: '11. Применимое право и контакты',
          body: [
            'К настоящим условиям применяется законодательство Республики Узбекистан. Споры решаются прежде всего путём переговоров.',
            contactLine((e) => `Вопросы: ${e}`, `По вопросам обращайтесь в администрацию Школы.`),
          ],
        },
      ],
    },

    privacy: {
      title: 'Политика конфиденциальности',
      updated: LEGAL_UPDATED,
      intro:
        `Политика объясняет, какие персональные данные собирают мобильное приложение ${APP_NAME} и панель управления школой (${LEGAL_SITE}) — вместе «Платформа», как они используются, кому передаются, сколько хранятся и как удаляются. Разработчик: ${DEVELOPER_NAME} (${DEVELOPER_URL}). На Платформе обрабатываются данные несовершеннолетних учеников — поэтому защите уделяется особое внимание.`,
      sections: [
        {
          title: '1. Приложение, разработчик и контролёр данных',
          body: [
            `Разработчик приложения ${APP_NAME} и Платформы — ${DEVELOPER_NAME} (${DEVELOPER_URL}). Постоянный контакт по вопросам конфиденциальности и удаления аккаунта: ${PRIVACY_CONTACT_EMAIL}.`,
            'Владелец и контролёр данных учеников, родителей и сотрудников — Школа. Мы выступаем обработчиком данных от имени Школы.',
            'Открытой регистрации нет: логин и пароль выдаёт Школа, она же управляет учётными записями.',
            'Запросы на исправление данных направляются в первую очередь в Школу.',
          ],
        },
        {
          title: '2. Какие данные мы собираем',
          body: [
            'Личные: Ф.И.О., пол, дата рождения, телефон, адрес, фото профиля, Telegram ID (если его внесла Школа).',
            'Учётные: логин, пароль (только в виде зашифрованного хеша — открытый пароль не храним), роль.',
            'Образовательные: класс, группа, посещаемость, оценки, домашние задания, расписание, баллы за поведение и тесты, рейтинг.',
            'Финансовые: договоры, платежи, задолженность, зарплата сотрудников.',
            'При онлайн-подписании договора родитель сам вводит: Ф.И.О., серию и номер паспорта, ПИНФЛ, адрес проживания, телефон и нарисованную от руки подпись. В качестве юридического доказательства также сохраняются IP-адрес и данные устройства/браузера (User-Agent) в момент подписания. При отказе от договора сохраняется указанная родителем причина.',
            'Чат поддержки: сообщения между родителем и администрацией Школы.',
            'Технические: токен push-уведомлений, настройки языка и темы, логи ошибок.',
            'Журнал аудита (безопасности): каждое изменяющее данные действие — кто выполнил (логин, роль), какое действие, время, IP-адрес и данные устройства/браузера (User-Agent).',
            'FACE ID (только для сотрудников Школы): терминал распознавания лиц установлен в здании Школы. Биометрический шаблон лица хранится только в памяти терминала и на сервер не передаётся; снимок, который терминал присылает вместе с событием, на сервере не сохраняется. На сервер поступает только событие прихода/ухода (номер сотрудника и время), а на терминал передаются имя и номер сотрудника.',
          ],
        },
        {
          title: '3. Разрешения приложения',
          body: [
            'Камера и галерея — только чтобы сделать или выбрать фото профиля. Фото загружается только когда вы сами его выбрали и подтвердили; камера в фоне не используется.',
            'Уведомления — об оценках, посещаемости, объявлениях, платежах и других сообщениях. Разрешение можно отключить в любой момент в настройках устройства.',
            'Приложение не запрашивает доступ к геолокации, контактам или микрофону.',
          ],
        },
        {
          title: '4. Зачем мы их используем',
          body: [
            'Оказание услуги: посещаемость, оценки, расписание, платежи и договоры.',
            'Отправка уведомлений (оценка, посещаемость, объявление, напоминание об оплате).',
            'Поддержка, безопасность (журнал аудита) и исправление ошибок.',
            'Мы НЕ используем данные в рекламных целях и НЕ продаём их третьим лицам.',
          ],
        },
        {
          title: '5. Данные детей',
          body: [
            `Данные учеников вносит Школа; мы не собираем их напрямую у детей. Приложением ${APP_NAME} пользуются родители и учителя.`,
            'Данные детей используются только для учебного процесса и видны только уполномоченным лицам (сотрудники Школы, родитель — по своему ребёнку).',
            'Данные детей никогда не используются для рекламы или профилирования.',
          ],
        },
        {
          title: '6. Кто имеет доступ',
          body: [
            'Доступ ограничен РОЛЬЮ: родитель видит только своего ребёнка, учитель — только свой класс/группу.',
            'Рейтинг: в таблице рейтинга, где участвует ребёнок, родителю видны имя, класс и балл других учеников; для одноклассников ребёнка также видны фото профиля и детали рейтинга (средняя оценка, количество оценок, баллы по предметам, посещаемости, поведению, тестам и домашним заданиям).',
            'Если Школа включила экран в здании (киоск), на нём показываются рейтинг учеников и именинники (имя, класс, фото), расписание и объявления.',
            'Данные каждой Школы технически ИЗОЛИРОВАНЫ от других — одна школа не может видеть данные другой.',
          ],
        },
        {
          title: '7. Сторонние сервисы',
          body: [
            'Сервер, база данных и файловое хранилище (фото профиля, документы): Railway (с S3-совместимым объектным хранилищем).',
            'Доменные имена (DNS): Cloudflare.',
            'Push-уведомления: сервис Expo Push и через него Google Firebase Cloud Messaging (Android) и Apple Push Notification service (iOS) — передаются токен устройства и текст уведомления.',
            'SMS (если включено для Школы, например ссылка на договор): Eskiz.uz — передаются номер телефона и текст SMS.',
            'Telegram: ежедневная резервная копия базы данных и технические оповещения отправляются в закрытые Telegram-группы; Школа может включить отправку резервной копии своих данных в собственный Telegram-бот.',
            'Отслеживание ошибок (если включено): Sentry — только сведения об ошибках сервера.',
            'Данные передаются только в объёме, необходимом для оказания услуги. Рекламным и аналитическим сетям они не передаются и не продаются.',
          ],
        },
        {
          title: '8. Срок хранения',
          body: [
            'Данные хранятся в течение действия договора со Школой или пока Школа их не удалит.',
            'Журнал аудита (безопасности) — автоматически удаляется через 90 дней.',
            'Токен push-уведомлений — удаляется, когда обнаружено удаление приложения, или при запросе на удаление аккаунта.',
            'Подписанный договор (с подписью, IP-адресом и User-Agent) хранится как документ Школы в срок, установленный Школой.',
            'Резервные копии хранятся в закрытых Telegram-группах и автоматически не удаляются — их очищает администрация в установленном ею порядке. Удалённые данные могут оставаться в резервных копиях до их очистки.',
            'После завершения договора со Школой данные возвращаются Школе или удаляются в разумный срок (кроме случаев, требуемых законом).',
          ],
        },
        {
          title: '9. Удаление аккаунта и данных',
          body: [
            'В приложении: Профиль → Аккаунт → «Удалить аккаунт» (с подтверждением паролем).',
            `Без приложения: на странице ${DELETE_ACCOUNT_URL} с вашим логином и паролем.`,
            'Сразу после отправки запроса аккаунт закрывается: войти в него нельзя, push-уведомления прекращаются. Администрация Школы завершает запрос в течение 30 дней.',
            'Удаляются: логин, пароль, имя, телефон, пол, дата рождения, фото профиля, Telegram ID, дополнительные телефоны и адреса, push-токены, уведомления в приложении и переписка в чате поддержки.',
            'Остаются как записи Школы (вместо имени — «O‘chirilgan foydalanuvchi», т. е. удалённый пользователь): оценки, посещаемость, платежи и договоры ученика (с данными родителя в подписанном договоре), контакт родителя в карточке ученика; для учителя — выставленные оценки, домашние задания, расписание, записи о зарплате и рабочем времени.',
            'Журнал аудита удаляется через 90 дней, резервные копии — при их очистке. Запись сотрудника в терминале FACE ID этим запросом не удаляется — её удаляет администрация Школы.',
            'Школа может отклонить запрос с указанием причины — тогда аккаунт возвращается в прежнее состояние, а вы получите сообщение с причиной.',
            'Чтобы удалить или исправить данные вашего ребёнка (ученика), обратитесь в администрацию Школы.',
          ],
        },
        {
          title: '10. Безопасность',
          body: [
            'Пароли хранятся в виде зашифрованного хеша (bcrypt).',
            'Весь обмен данными шифруется по HTTPS.',
            'Применяется ролевой контроль доступа и изоляция данных между школами.',
            'Ни одна система не защищена на 100% — при подозрении сообщите нам немедленно.',
          ],
        },
        {
          title: '11. Ваши права',
          body: [
            'Вы вправе узнать, какие данные о вас (или вашем ребёнке) хранятся, исправить неточности и запросить удаление (удаление аккаунта — раздел 9).',
            'Уведомления можно отключить в любой момент в настройках приложения или устройства.',
            contactLine(
              (e) => `Запрос направьте администрации Школы, на ${e} или на ${PRIVACY_CONTACT_EMAIL}.`,
              `Запрос направьте администрации Школы или на ${PRIVACY_CONTACT_EMAIL}.`
            ),
          ],
        },
        {
          title: '12. Cookie и локальное хранилище',
          body: [
            'В веб-версии используется localStorage браузера, в приложении — память устройства. Подробнее — в разделе «Политика cookie».',
          ],
        },
        {
          title: '13. Изменения и контакты',
          body: [
            'Политика может обновляться; дата последнего обновления указана выше.',
            `Связь с разработчиком (${DEVELOPER_NAME}): ${PRIVACY_CONTACT_EMAIL}.`,
            contactLine((e) => `Связь с вашей школой: ${e}`, `По вопросам о вашей школе обращайтесь в администрацию Школы.`),
          ],
        },
      ],
    },

    cookies: {
      title: 'Политика cookie',
      updated: LEGAL_UPDATED,
      intro:
        `Политика объясняет, как в веб-панели ${APP_NAME} (${LEGAL_SITE}) используются cookie и хранилище браузера (localStorage), а в мобильном приложении — память устройства.`,
      sections: [
        {
          title: '1. Что такое cookie и локальное хранилище',
          body: [
            'Cookie — небольшой текстовый файл, который сайт сохраняет в браузере. localStorage — похожее хранилище браузера, управляемое сайтом.',
            'Мы в основном используем localStorage — чтобы «запомнить» ваш вход и сохранить настройки.',
          ],
        },
        {
          title: '2. Что мы храним',
          body: [
            'Токен входа (sata_token) — чтобы вы оставались в системе. НЕОБХОДИМО.',
            'Данные пользователя (sata_user) — для отображения имени/роли. НЕОБХОДИМО.',
            'Выбор языка и темы (светлая/тёмная) — для удобства.',
            'В приложении: токен входа, токен push-уведомлений и настройки хранятся в памяти устройства.',
          ],
        },
        {
          title: '3. Рекламы и трекинга НЕТ',
          body: [
            'Мы НЕ используем рекламные cookie, трекинг-пиксели или стороннюю аналитику.',
            'Ваши данные не передаются рекламным сетям.',
          ],
        },
        {
          title: '4. Третьи стороны',
          body: [
            'Cloudflare может устанавливать технические cookie для безопасности и скорости (защита от ботов). Это не трекинг.',
          ],
        },
        {
          title: '5. Как управлять',
          body: [
            'Вы можете очистить cookie/localStorage в настройках браузера.',
            'ВНИМАНИЕ: при удалении необходимого хранилища вы выйдете из системы и Платформа не будет работать корректно.',
            'В приложении — удалив приложение или очистив данные в настройках устройства.',
          ],
        },
        {
          title: '6. Согласие',
          body: [
            'Мы используем только НЕОБХОДИМОЕ хранилище — без него вход невозможен. Используя Платформу, вы соглашаетесь с этим.',
            contactLine((e) => `Вопросы: ${e}`, `По вопросам обращайтесь в администрацию Школы.`),
          ],
        },
      ],
    },
  },

  // ─────────────────────────── ENGLISH ───────────────────────────
  en: {
    terms: {
      title: 'Terms of Use',
      updated: LEGAL_UPDATED,
      intro:
        `${APP_NAME} ("the Platform") is a software service for school management (developer: ${DEVELOPER_NAME}, ${DEVELOPER_URL}). By using the Platform you agree to these terms. If you do not agree, do not use the service.`,
      sections: [
        {
          title: '1. Definitions',
          body: [
            `"Platform" — the ${APP_NAME} mobile app and the ${LEGAL_SITE} management panel.`,
            '"School" — the educational institution using the Platform.',
            '"User" — a person given an account by the School (director, admin, accountant, teacher, parent).',
            '"Content" — any data entered into the Platform (student data, grades, attendance, payments, etc.).',
          ],
        },
        {
          title: '2. Service description',
          body: [
            'The Platform lets a school manage students, classes, schedules, attendance, grades, homework, payments, expenses and staff.',
            'The service is provided "as is". We aim for continuous availability, but temporary interruptions may occur due to maintenance or third-party services.',
          ],
        },
        {
          title: '3. Accounts and access',
          body: [
            'User accounts are created and managed by the School. There is no public sign-up.',
            'Keeping your login and password confidential is your responsibility. You are liable for all actions taken under your account.',
            'If you suspect your account is compromised, notify the School administration immediately.',
          ],
        },
        {
          title: '4. User obligations',
          body: [
            'Access only data within your role and use it solely for educational purposes.',
            'Do not distribute, copy or disclose other people’s personal data to third parties.',
            'The User who enters data and the School are responsible for its accuracy.',
          ],
        },
        {
          title: '5. Prohibited actions',
          body: [
            'Attempting unauthorised access, exploiting vulnerabilities, automated scraping, or overloading the system.',
            'Acting on behalf of another user, transferring or selling an account.',
            'Posting unlawful, abusive, or child-harmful content.',
          ],
        },
        {
          title: '6. Intellectual property',
          body: [
            'The Platform’s code, design and brand belong to us. Copying or reuse is prohibited.',
            'Content entered by the School remains the School’s property. We use it only to provide the service.',
          ],
        },
        {
          title: '7. Payments and subscription',
          body: [
            'Use of the Platform is paid under an agreement with the School.',
            'Payment terms, periods and cancellation are agreed separately. Non-payment may lead to suspension.',
          ],
          premiumBody: [
            'Optional extras (Premium) may be offered to parents.',
          ],
        },
        {
          title: '8. Limitation of liability',
          body: [
            'We are not responsible for the accuracy of Content (grades, attendance, payment amounts) — it is entered by the School.',
            'Liability for indirect damages, lost profit or data loss is limited to the extent permitted by law.',
            'We are not liable for damage caused by a User disclosing their own password.',
          ],
        },
        {
          title: '9. Suspension',
          body: [
            'An account violating these terms may be blocked without notice.',
            'If the School terminates the agreement, data is deleted or returned within the period stated in the Privacy Policy.',
          ],
        },
        {
          title: '10. Changes',
          body: [
            'These terms may be updated from time to time. We will announce material changes via the Platform.',
            'Continued use after an update means acceptance of the new terms.',
          ],
        },
        {
          title: '11. Governing law and contact',
          body: [
            'These terms are governed by the law of the Republic of Uzbekistan. Disputes are resolved primarily through negotiation.',
            contactLine((e) => `Questions: ${e}`, `For questions, please contact the School administration.`),
          ],
        },
      ],
    },

    privacy: {
      title: 'Privacy Policy',
      updated: LEGAL_UPDATED,
      intro:
        `This policy explains what personal data the ${APP_NAME} mobile app and the school management panel (${LEGAL_SITE}) — together "the Platform" — collect, how it is used, who it is shared with, how long it is kept and how it is deleted. Developer: ${DEVELOPER_NAME} (${DEVELOPER_URL}). The Platform processes data of underage students — so we pay special attention to protection.`,
      sections: [
        {
          title: '1. App, developer and data controller',
          body: [
            `The ${APP_NAME} app and the Platform are developed by ${DEVELOPER_NAME} (${DEVELOPER_URL}). Permanent contact for privacy and account deletion: ${PRIVACY_CONTACT_EMAIL}.`,
            'The School is the owner and controller of student, parent and staff data. We act as a data processor on the School’s behalf.',
            'There is no public sign-up: the School issues logins and passwords and manages accounts.',
            'Requests to correct data should first be sent to the School.',
          ],
        },
        {
          title: '2. Data we collect',
          body: [
            'Personal: full name, gender, date of birth, phone, address, profile photo, Telegram ID (if entered by the School).',
            'Account: login, password (stored only as an encrypted hash — we never store plain passwords), role.',
            'Educational: class, group, attendance, grades, homework, schedule, behaviour and test scores, ranking.',
            'Financial: contracts, payments, debts, staff salaries.',
            'When signing a contract online, the parent enters: full name, passport series and number, PINFL (personal ID number), home address, phone and a hand-drawn signature image. As legal evidence, the IP address and device/browser information (User-Agent) at the time of signing are also stored. If the contract is declined, the reason given by the parent is stored.',
            'Support chat: messages between the parent and the School administration.',
            'Technical: push notification token, language and theme settings, error logs.',
            'Audit (security) log: every action that changes data — who performed it (login, role), which action, time, IP address and device/browser information (User-Agent).',
            'FACE ID (School staff only): a face recognition terminal is installed in the School building. The biometric face template is stored only in the terminal’s memory and is not sent to the server; the photo the terminal sends with an event is not stored on the server. The server receives only arrival/departure events (staff number and time), and the staff member’s name and number are sent to the terminal.',
          ],
        },
        {
          title: '3. App permissions',
          body: [
            'Camera and gallery — only to take or choose a profile photo. The photo is uploaded only when you select and confirm it; the camera is never used in the background.',
            'Notifications — for grades, attendance, announcements, payments and other messages. You can turn the permission off at any time in device settings.',
            'The app does not request location, contacts or microphone access.',
          ],
        },
        {
          title: '4. Why we use it',
          body: [
            'To provide the service: attendance, grades, schedule, payments and contracts.',
            'To send notifications (grade, attendance, announcement, payment reminder).',
            'For support, security (audit log) and bug fixing.',
            'We do NOT use data for advertising and do NOT sell it to third parties.',
          ],
        },
        {
          title: '5. Children’s data',
          body: [
            `Student data is entered by the School; we do not collect it directly from children. The ${APP_NAME} app is used by parents and teachers.`,
            'Children’s data is used only for the educational process and is visible only to authorised people (School staff; a parent — for their own child).',
            'Children’s data is never used for advertising or profiling.',
          ],
        },
        {
          title: '6. Who can access it',
          body: [
            'Access is restricted by ROLE: a parent sees only their child; a teacher only their class/group.',
            'Ranking: in a ranking table that includes their child, a parent sees other students’ names, classes and scores; for the child’s classmates the profile photo and ranking details (average grade, number of grades, scores for subjects, attendance, behaviour, tests and homework) are also visible.',
            'If the School enables an in-building screen (kiosk), it shows the student ranking and birthdays (name, class, photo), the schedule and announcements.',
            'Each School’s data is technically ISOLATED from others — one school cannot see another’s data.',
          ],
        },
        {
          title: '7. Third-party services',
          body: [
            'Server, database and file storage (profile photos, documents): Railway (with S3-compatible object storage).',
            'Domain names (DNS): Cloudflare.',
            'Push notifications: the Expo Push service and, through it, Google Firebase Cloud Messaging (Android) and Apple Push Notification service (iOS) — the device token and notification text are shared.',
            'SMS (if enabled for the School, e.g. a contract link): Eskiz.uz — the phone number and SMS text are shared.',
            'Telegram: the daily database backup and technical alerts are sent to private Telegram groups; a School may enable sending a backup of its own data to its own Telegram bot.',
            'Error tracking (if enabled): Sentry — server error details only.',
            'Data is shared only to the extent needed to provide the service. It is not shared with or sold to advertising or analytics networks.',
          ],
        },
        {
          title: '8. Retention',
          body: [
            'Data is kept for the duration of the agreement with the School or until the School deletes it.',
            'Audit (security) log — automatically deleted after 90 days.',
            'Push notification token — deleted when the app is detected as uninstalled or when account deletion is requested.',
            'A signed contract (with signature, IP address and User-Agent) is kept as a School document for the period set by the School.',
            'Backups are kept in private Telegram groups and are not deleted automatically — they are cleaned up by the administration according to its own schedule. Deleted data may remain in backups until they are cleaned up.',
            'After the agreement with the School ends, data is returned to the School or deleted within a reasonable period (except where the law requires otherwise).',
          ],
        },
        {
          title: '9. Deleting your account and data',
          body: [
            'In the app: Profile → Account → “Delete account” (confirmed with your password).',
            `Without the app: on ${DELETE_ACCOUNT_URL} with your login and password.`,
            'As soon as the request is sent, the account is closed: you can no longer sign in and push notifications stop. The School administration completes the request within 30 days.',
            'Deleted: login, password, name, phone, gender, date of birth, profile photo, Telegram ID, additional phones and addresses, push tokens, in-app notifications and support chat messages.',
            'Kept as School records (with “O‘chirilgan foydalanuvchi”, i.e. deleted user, instead of the name): the student’s grades, attendance, payments and contracts (including the parent details in a signed contract), the parent contact on the student card; for a teacher — grades given, homework, schedule, salary and working-time records.',
            'The audit log is deleted after 90 days and backups when they are cleaned up. A staff member’s FACE ID terminal record is not removed by this request — the School administration removes it.',
            'The School may decline the request with a reason — the account then returns to its previous state and you receive a message with the reason.',
            'To delete or correct your child’s (student’s) data, contact the School administration.',
          ],
        },
        {
          title: '10. Security',
          body: [
            'Passwords are stored as an encrypted hash (bcrypt).',
            'All communication is encrypted over HTTPS.',
            'Role-based access control and cross-school data isolation are applied.',
            'No system is 100% secure — please report anything suspicious immediately.',
          ],
        },
        {
          title: '11. Your rights',
          body: [
            'You may ask what data we hold about you (or your child), correct inaccuracies and request deletion (account deletion — section 9).',
            'You can turn notifications off at any time in the app or device settings.',
            contactLine(
              (e) => `Send requests to the School administration, to ${e} or to ${PRIVACY_CONTACT_EMAIL}.`,
              `Send requests to the School administration or to ${PRIVACY_CONTACT_EMAIL}.`
            ),
          ],
        },
        {
          title: '12. Cookies and local storage',
          body: [
            'The web version uses browser localStorage; the mobile app uses device storage. See the "Cookie Policy" for details.',
          ],
        },
        {
          title: '13. Changes and contact',
          body: [
            'This policy may be updated; the last-updated date is shown above.',
            `Contact the developer (${DEVELOPER_NAME}): ${PRIVACY_CONTACT_EMAIL}.`,
            contactLine((e) => `Contact your school: ${e}`, `For questions about your school, please contact the School administration.`),
          ],
        },
      ],
    },

    cookies: {
      title: 'Cookie Policy',
      updated: LEGAL_UPDATED,
      intro:
        `This policy explains how the ${APP_NAME} web panel (${LEGAL_SITE}) uses cookies and browser storage (localStorage), and how the mobile app uses device storage.`,
      sections: [
        {
          title: '1. What cookies and local storage are',
          body: [
            'A cookie is a small text file a site stores in your browser. localStorage is similar browser storage controlled by the site.',
            'We mainly use localStorage — to keep you signed in and to remember your settings.',
          ],
        },
        {
          title: '2. What we store',
          body: [
            'Sign-in token (sata_token) — to keep you logged in. ESSENTIAL.',
            'User data (sata_user) — to show your name/role. ESSENTIAL.',
            'Language and theme (light/dark) preference — for convenience.',
            'In the app: sign-in token, push notification token and settings are stored in device storage.',
          ],
        },
        {
          title: '3. NO advertising or tracking',
          body: [
            'We do NOT use advertising cookies, tracking pixels or third-party analytics.',
            'Your data is not shared with ad networks.',
          ],
        },
        {
          title: '4. Third parties',
          body: [
            'Cloudflare may set technical cookies for security and speed (bot protection). This is not tracking.',
          ],
        },
        {
          title: '5. How to manage',
          body: [
            'You can clear cookies/localStorage in your browser settings.',
            'NOTE: clearing essential storage will sign you out and the Platform will not work correctly.',
            'In the app — by uninstalling it or clearing app data in device settings.',
          ],
        },
        {
          title: '6. Consent',
          body: [
            'We use only ESSENTIAL storage — sign-in is impossible without it. By using the Platform you consent to this.',
            contactLine((e) => `Questions: ${e}`, `For questions, please contact the School administration.`),
          ],
        },
      ],
    },
  },
};

/**
 * PREMIUM MUZLATISH: bayroq yoqilgan bo'lsa `premiumBody` bandlari `body` oxiriga
 * qo'shiladi, o'chiq bo'lsa hujjat o'zgarishsiz qaytadi (sahifalar faqat `body` ni
 * chizadi). Bir marta — modul yuklanganda — hisoblanadi: `PREMIUM_UI_ENABLED` doimiy,
 * `LEGAL` esa o'zgarmaydi, shuning uchun har renderda yangi massiv yasashning hojati yo'q.
 */
function withPremiumSections(
  src: Record<Lang, Record<LegalKey, LegalDocSource>>
): Record<Lang, Record<LegalKey, LegalDocSource>> {
  const out = {} as Record<Lang, Record<LegalKey, LegalDocSource>>;
  for (const lang of Object.keys(src) as Lang[]) {
    const byKey = {} as Record<LegalKey, LegalDocSource>;
    for (const key of Object.keys(src[lang]) as LegalKey[]) {
      const doc = src[lang][key];
      byKey[key] = {
        ...doc,
        sections: doc.sections.map((s) =>
          s.premiumBody?.length ? { ...s, body: [...s.body, ...s.premiumBody] } : s
        ),
      };
    }
    out[lang] = byKey;
  }
  return out;
}

const LEGAL_VISIBLE: Record<Lang, Record<LegalKey, LegalDocSource>> =
  PREMIUM_UI_ENABLED ? withPremiumSections(LEGAL) : LEGAL;

/** Qatorni matnga aylantiradi: email bo'lsa kontaktli, bo'lmasa neytral variant. */
const resolveLine = (line: LegalLine, email: string | null): string =>
  typeof line === 'string' ? line : email ? line.withContact(email) : line.without;

/**
 * Joriy i18n tiliga mos hujjat (noma'lum til — uz).
 * `contactEmail` — JORIY sessiya maktabining support email'i (`GET /tenant/support-email`).
 * `null` bo'lsa kontakt qatorlari neytral matn bilan chiqadi — zaxira manzil YO'Q.
 */
export function getLegalDoc(lang: string, key: LegalKey, contactEmail: string | null): LegalDoc {
  const l: Lang = lang === 'ru' || lang === 'en' ? lang : 'uz';
  const doc = LEGAL_VISIBLE[l][key];
  const email = contactEmail?.trim() || null;
  return {
    title: doc.title,
    updated: doc.updated,
    intro: doc.intro,
    sections: doc.sections.map((s) => ({ title: s.title, body: s.body.map((ln) => resolveLine(ln, email)) })),
  };
}
